import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import * as topojson from "topojson-client";
import "../styles/map.css";
import { CountryCodeMapping } from "../constant/countryData";

// European Patent Office member countries
const EPCountries = [
  "AT",
  "BE",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GB",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MC",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  "SM",
  "TR",
];

interface Patent {
  application_number: string;
  country_code: string;
  filing_date: string;
  legal_status: string;
}

interface PatentMapProps {
  patents: Patent[];
}

interface Tooltip {
  visible: boolean;
  x: number;
  y: number;
  content: {
    countryName: string;
    activeCount: number;
    inactiveCount: number;
    intensityLevel: number;
  };
}

interface CountryStatus {
  active: number;
  inactive: number;
}

export function StandalonePatentMap({ patents }: PatentMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<Tooltip>({
    visible: false,
    x: 0,
    y: 0,
    content: {
      countryName: "",
      activeCount: 0,
      inactiveCount: 0,
      intensityLevel: 0,
    },
  });

  // Zoom state and refs
  const [zoomScale, setZoomScale] = useState(1);
  const zoomRef = useRef<any>(null);

  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 500,
  });

  // Process patent data to create a mapping of country codes to status counts
  const processPatentData = () => {
    const countryStatusMap: Record<string, CountryStatus> = {};

    patents.forEach((patent) => {
      const { country_code, legal_status } = patent;

      // Handle special case for EP (European Patent Office)
      if (country_code === "EP") {
        EPCountries.forEach((epCountry) => {
          if (!countryStatusMap[epCountry]) {
            countryStatusMap[epCountry] = { active: 0, inactive: 0 };
          }

          if (legal_status === "active") {
            countryStatusMap[epCountry].active += 1;
          } else {
            countryStatusMap[epCountry].inactive += 1;
          }
        });
      }
      // WO (World Intellectual Property Organization) is not mapped to specific countries
      else if (country_code !== "WO") {
        if (!countryStatusMap[country_code]) {
          countryStatusMap[country_code] = { active: 0, inactive: 0 };
        }

        if (legal_status === "active") {
          countryStatusMap[country_code].active += 1;
        } else {
          countryStatusMap[country_code].inactive += 1;
        }
      }
    });

    return countryStatusMap;
  };

  // Calculate the intensity level based on active count
  const getIntensityLevel = (activeCount: number): number => {
    if (activeCount <= 0) return 0;
    if (activeCount === 1) return 1;
    if (activeCount <= 2) return 1;
    if (activeCount <= 5) return 2;
    if (activeCount <= 10) return 3;
    if (activeCount <= 20) return 4;
    if (activeCount <= 50) return 5;
    return 5; // Maximum intensity level
  };

  // Get country CSS class based on status and intensity
  const getCountryClass = (
    countryCode: string,
    countryStatusMap: Record<string, CountryStatus>
  ): string => {
    if (countryStatusMap[countryCode]) {
      const { active } = countryStatusMap[countryCode];

      if (active > 0) {
        const level = getIntensityLevel(active);
        return `country country-active-level-${level}`;
      } else if (countryStatusMap[countryCode].inactive > 0) {
        return "country country-inactive";
      }
    }
    return "country country-nodata";
  };

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: 500,
        });
      }
    };

    // Initial call
    handleResize();

    // Add listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Zoom functions
  const handleZoomIn = () => {
    if (svgRef.current && zoomRef.current) {
      const newScale = zoomScale * 1.3;
      setZoomScale(newScale);
      d3.select(svgRef.current)
        .transition()
        .duration(300)
        .call(zoomRef.current.scaleTo, newScale);
    }
  };

  const handleZoomOut = () => {
    if (svgRef.current && zoomRef.current) {
      const newScale = Math.max(0.5, zoomScale / 1.3);
      setZoomScale(newScale);
      d3.select(svgRef.current)
        .transition()
        .duration(300)
        .call(zoomRef.current.scaleTo, newScale);
    }
  };

  const handleZoomReset = () => {
    if (svgRef.current && zoomRef.current) {
      setZoomScale(1);
      d3.select(svgRef.current)
        .transition()
        .duration(300)
        .call(zoomRef.current.transform, d3.zoomIdentity);
    }
  };

  // Draw map
  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0) return;

    const countryStatusMap = processPatentData();

    // Clear previous map
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current);

    // Create projection
    const projection = d3
      .geoNaturalEarth1()
      .scale(dimensions.width / 5.5)
      .translate([dimensions.width / 2, dimensions.height / 2]);

    const path = d3.geoPath().projection(projection);

    // Create zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 8])
      .on("zoom", (event: any) => {
        svg.selectAll("path").attr("transform", event.transform);
        setZoomScale(event.transform.k);
      });

    // Store zoom reference
    zoomRef.current = zoom;

    // Apply zoom behavior to SVG
    svg.call(zoom);

    // Load world map data
    d3.json(
      "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json"
    ).then((data: any) => {
      const countries = topojson.feature(data, data.objects.countries).features;

      // Create a reverse mapping for easier lookup - name to code
      const nameToCodeMap: Record<string, string> = {};
      for (const [code, name] of Object.entries(CountryCodeMapping)) {
        nameToCodeMap[name] = code;
      }

      // Helper to get country name and code from topojson ID
      const getCountryInfo = (id: number): { code: string; name: string } => {
        const country = countries.find((c: any) => c.id === id);

        if (country && country.properties) {
          const countryName = country.properties.name;

          // Special case for United States
          if (countryName === "United States of America") {
            return { code: "US", name: "United States" };
          }

          // First try a direct name lookup
          if (nameToCodeMap[countryName]) {
            return { code: nameToCodeMap[countryName], name: countryName };
          }

          // Try to match country name to our code mapping
          for (const [code, name] of Object.entries(CountryCodeMapping)) {
            if (name === countryName) {
              return { code, name: countryName };
            }
          }

          // If we don't have a code mapping but have a name, return the name with unknown code
          if (countryName) {
            return { code: "Unknown", name: countryName };
          }
        }

        return { code: "Unknown", name: "Unknown Country" };
      };

      svg
        .selectAll("path")
        .data(countries)
        .enter()
        .append("path")
        .attr("d", path as any)
        .attr("class", (d: any) => {
          const { code } = getCountryInfo(d.id);
          return getCountryClass(code, countryStatusMap);
        })
        .attr("stroke-width", 0.4)
        .on(
          "mouseover",
          function (this: SVGPathElement, event: MouseEvent, d: any) {
            // Get country info
            const { code, name } = getCountryInfo(d.id);

            // Get status counts
            let activeCount = 0;
            let inactiveCount = 0;
            let intensityLevel = 0;

            if (countryStatusMap[code]) {
              activeCount = countryStatusMap[code].active;
              inactiveCount = countryStatusMap[code].inactive;
              intensityLevel = getIntensityLevel(activeCount);
            }

            // Show tooltip
            setTooltip({
              visible: true,
              x: event.pageX,
              y: event.pageY,
              content: {
                countryName: name,
                activeCount,
                inactiveCount,
                intensityLevel,
              },
            });

            // Increase stroke width on hover
            d3.select(this).attr("stroke-width", 0.8);
          }
        )
        .on("mousemove", function (this: SVGPathElement, event: MouseEvent) {
          // Position tooltip directly at cursor position
          setTooltip((prev) => ({
            ...prev,
            x: event.clientX,
            y: event.clientY,
          }));
        })
        .on("mouseout", function (this: SVGPathElement) {
          // Hide tooltip
          setTooltip((prev) => ({
            ...prev,
            visible: false,
          }));

          // Restore normal stroke width
          d3.select(this).attr("stroke-width", 0.4);
        });
    });
  }, [dimensions, patents]);

  return (
    <div ref={containerRef} className="patent-map-container">
      {/* Zoom Controls */}
      <div className="zoom-controls">
        <button
          onClick={handleZoomIn}
          className="zoom-btn"
          aria-label="Zoom in"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
        <button
          onClick={handleZoomOut}
          className="zoom-btn"
          aria-label="Zoom out"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
        <button
          onClick={handleZoomReset}
          className="zoom-btn"
          aria-label="Reset zoom"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
            <path d="M3 3v5h5"></path>
          </svg>
        </button>
      </div>

      {/* Simplified Tooltip with only active and inactive counts */}
      {tooltip.visible && (
        <div
          className="map-tooltip"
          style={{
            left: `${tooltip.x - 200}px`,
            top: `${tooltip.y - 70}px`,
          }}
        >
          <div className="tooltip-country">{tooltip.content.countryName}</div>
          <div className="tooltip-row">
            <span className="tooltip-label">Active:</span>
            <span className="tooltip-value tooltip-status-active">
              {tooltip.content.activeCount}
            </span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Non-active:</span>
            <span className="tooltip-value tooltip-status-inactive">
              {tooltip.content.inactiveCount}
            </span>
          </div>
        </div>
      )}

      {/* Map */}
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        className="patent-map-svg"
      />
    </div>
  );
}
